import React, { createContext, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';

export const FilterDataContext = createContext<DataContextProps>({} as DataContextProps);

const { Provider } = FilterDataContext;

interface IObjectKeys {
  [key: string]: boolean;
}

interface IAllStatesCheck extends IObjectKeys {
  1000: boolean;
  2000: boolean;
  5000: boolean;
  7000: boolean;
  10000: boolean;
  20000: boolean;
}

interface IAllStatesAvailableCheck extends IObjectKeys {
  0: boolean;
  1000: boolean;
  2000: boolean;
  5000: boolean;
  7000: boolean;
  10000: boolean;
  20000: boolean;
}

interface DataContextProps {
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  startInclusionDate: Dayjs | null;
  setStartInclusionDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
  endInclusionDate: Dayjs | null;
  setEndInclusionDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
  allStatesCheck: IAllStatesCheck;
  setAllStatesCheck: React.Dispatch<React.SetStateAction<IAllStatesCheck>>;
  allStatesCheckEngaged: IAllStatesCheck;
  setAllStatesCheckEngaged: React.Dispatch<React.SetStateAction<IAllStatesCheck>>;
  allStatesAvailableCheck: IAllStatesCheck;
  setAllStatesAvailableCheck: React.Dispatch<React.SetStateAction<IAllStatesAvailableCheck>>;
  allStatesAvailableCheckEngaged: IAllStatesAvailableCheck;
  setAllStatesAvailableCheckEngaged: React.Dispatch<React.SetStateAction<IAllStatesAvailableCheck>>;
  sortType: string;
  setSortType: React.Dispatch<React.SetStateAction<string>>;
  isSortAsc: boolean;
  setIsSortAsc: React.Dispatch<React.SetStateAction<boolean>>;
  filterButtonPressed: boolean;
  setFilterButtonPressed: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedContractedPlan: React.Dispatch<React.SetStateAction<string[]>>;
  selectedContractedPlan: string[];
  selectedAvailablePlan: string[];
  setSelectedAvailablePlan: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedStartDate: React.Dispatch<React.SetStateAction<dayjs.Dayjs | null>>;
  selectedStartDate: Dayjs | null;
  setSelectedEndDate: React.Dispatch<React.SetStateAction<dayjs.Dayjs | null>>;
  selectedEndDate: Dayjs | null;
  setVerifyExcludeAllPlans: React.Dispatch<React.SetStateAction<boolean>>;
  verifyExcludeAllPlans: boolean;
  startRowNum: number;
  setStartRowNum: React.Dispatch<React.SetStateAction<number>>;
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
}

interface ChildrenProps {
  children?: React.ReactNode;
}

const DataProvider = ({ children }: ChildrenProps) => {
  const [loading, setLoading] = useState(true);
  const [selectedContractedPlan, setSelectedContractedPlan] = useState<string[]>([]);
  const [selectedAvailablePlan, setSelectedAvailablePlan] = useState<string[]>([]);
  const [verifyExcludeAllPlans, setVerifyExcludeAllPlans] = useState<boolean>(false);
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | null>(null);
  const [startRowNum, setStartRowNum] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs | null>(null);
  const [sortType, setSortType] = useState('status');
  const [isSortAsc, setIsSortAsc] = useState(true);
  const [startInclusionDate, setStartInclusionDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endInclusionDate, setEndInclusionDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [allStatesCheck, setAllStatesCheck] = useState<IAllStatesCheck>({
    '1000': false,
    '2000': false,
    '5000': false,
    '7000': false,
    '10000': false,
    '20000': false,
  });
  const [allStatesCheckEngaged, setAllStatesCheckEngaged] = useState<IAllStatesCheck>({
    '1000': false,
    '2000': false,
    '5000': false,
    '7000': false,
    '10000': false,
    '20000': false,
  });
  const [allStatesAvailableCheck, setAllStatesAvailableCheck] = useState<IAllStatesAvailableCheck>({
    '0': false,
    '1000': false,
    '2000': false,
    '5000': false,
    '7000': false,
    '10000': false,
    '20000': false,
  });
  const [allStatesAvailableCheckEngaged, setAllStatesAvailableCheckEngaged] = useState<IAllStatesAvailableCheck>({
    '0': false,
    '1000': false,
    '2000': false,
    '5000': false,
    '7000': false,
    '10000': false,
    '20000': false,
  });
  const [filterButtonPressed, setFilterButtonPressed] = useState(false);

  const dataValues: DataContextProps = {
    sortType,
    setSortType,
    isSortAsc,
    setIsSortAsc,
    startInclusionDate,
    setStartInclusionDate,
    endInclusionDate,
    setEndInclusionDate,
    allStatesCheck,
    setAllStatesCheck,
    allStatesCheckEngaged,
    setAllStatesCheckEngaged,
    allStatesAvailableCheck,
    setAllStatesAvailableCheck,
    allStatesAvailableCheckEngaged,
    setAllStatesAvailableCheckEngaged,
    filterButtonPressed,
    setFilterButtonPressed,
    selectedContractedPlan,
    setSelectedContractedPlan,
    selectedAvailablePlan,
    setSelectedAvailablePlan,
    setSelectedStartDate,
    selectedStartDate,
    setSelectedEndDate,
    selectedEndDate,
    setVerifyExcludeAllPlans,
    verifyExcludeAllPlans,
    startRowNum,
    setStartRowNum,
    search,
    setSearch,
    loading,
    setLoading,
  };

  return <Provider value={dataValues}>{children}</Provider>;
};

export default DataProvider;
